import { useState, useEffect, useRef, lazy, Suspense } from "react";
import Navbar from "./components/Navbar";
import HeroCarousel from "./components/HeroCarousel";
import SchemaJsonLD from "../../components/feature/SchemaJsonLD";

// Lazy load below-fold sections to reduce initial bundle parse time
const NewsSection = lazy(() => import("./components/NewsSection"));
const ProjectsSection = lazy(() => import("./components/ProjectsSection"));
const AboutUsSection = lazy(() => import("./components/AboutUsSection"));
const ServicesSection = lazy(() => import("./components/ServicesSection"));
const ContactSection = lazy(() => import("./components/ContactSection"));
const Footer = lazy(() => import("./components/Footer"));

const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://example.com";
const faviconUrl = "https://static.readdy.ai/image/c8608e961562513b854fa363830f8188/62b68f82ed14419242c6a505c0f43d79.png";

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "YEAHSTUDIO 隐也设计工作室",
  "alternateName": "YEAHSTUDIO 隐也建筑设计咨询（上海）有限公司",
  "url": siteUrl,
  "inLanguage": "zh-CN",
  "description": "YEAHSTUDIO（隐也建筑设计咨询（上海）有限公司）是一家位于上海的建筑与室内设计事务所，我们致力为客户量身打造创意设计、创新及巧妙解决方案的多元设计事务所。",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${siteUrl}/?s={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["ProfessionalService", "ArchitectureFirm"],
  "name": "YEAHSTUDIO 隐也设计工作室",
  "alternateName": "YEAHSTUDIO 隐也建筑设计咨询（上海）有限公司",
  "url": siteUrl,
  "logo": {
    "@type": "ImageObject",
    "url": faviconUrl,
    "contentUrl": faviconUrl
  },
  "image": faviconUrl,
  "description": "YEAHSTUDIO（隐也建筑设计咨询（上海）有限公司）是一家位于上海的建筑与室内设计事务所，我们致力为客户量身打造创意设计、创新及巧妙解决方案的多元设计事务所。我们非常注重空间的感受，并且善于运用空间的比例与尺度，依据每个项目的不同的属性，通过细节、光影与材质，在设计中注入叙事性，创造一段美妙独特的体验和美学空间氛围。",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "栖林路425弄7号",
    "addressLocality": "嘉定区",
    "addressRegion": "上海",
    "addressCountry": "CN"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 31.2304,
    "longitude": 121.4737
  },
  "telephone": "+86-189-1826-8808",
  "email": "wextin@foxmail.com",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+86-189-1826-8808",
    "email": "wextin@foxmail.com",
    "contactType": "customer service",
    "areaServed": "CN",
    "availableLanguage": ["Chinese", "English"]
  },
  "areaServed": "CN",
  "serviceType": ["零售设计", "品牌空间设计", "商业设计", "设计管理", "酒店设计", "餐饮设计", "住宅设计", "会所设计", "建筑及空间设计"],
  "knowsAbout": ["零售设计", "品牌空间设计", "商业设计", "设计管理", "酒店设计", "住宅设计", "建筑设计"],
  "sameAs": [
    "https://www.instagram.com/yeahstudio",
    "https://www.linkedin.com/company/yeahstudio"
  ]
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "YEAHSTUDIO | 隐也设计工作室 — 零售设计·品牌空间·商业设计·酒店餐饮住宅·建筑空间",
  "description": "YEAHSTUDIO（隐也建筑设计咨询（上海）有限公司）是一家位于上海的建筑与室内设计事务所，专注零售设计、品牌空间、商业设计、酒店餐饮住宅设计及建筑空间设计，通过细节、光影与材质注入叙事性，创造独特的空间体验与美学氛围。",
  "url": siteUrl,
  "inLanguage": "zh-CN",
  "isPartOf": {
    "@type": "WebSite",
    "name": "YEAHSTUDIO 隐也设计工作室",
    "url": siteUrl
  },
  "about": {
    "@type": "ArchitectureFirm",
    "name": "YEAHSTUDIO 隐也设计工作室"
  },
  "dateModified": "2026-04-11",
  "datePublished": "2024-01-01",
  "lastReviewed": "2026-04-11",
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": siteUrl
      }
    ]
  }
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "ArchitectureFirm"],
  "name": "YEAHSTUDIO 隐也设计工作室",
  "alternateName": "隐也建筑设计咨询（上海）有限公司",
  "url": siteUrl,
  "logo": faviconUrl,
  "image": faviconUrl,
  "description": "YEAHSTUDIO 是一家位于上海的建筑与室内设计事务所，专注商业空间、文化建筑与住宅设计，通过细节、光影与材质注入叙事性，创造独特的空间体验与美学氛围。",
  "telephone": "+86-189-1826-8808",
  "email": "wextin@foxmail.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "栖林路425弄7号",
    "addressLocality": "嘉定区",
    "addressRegion": "上海市",
    "postalCode": "201800",
    "addressCountry": "CN"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 31.2304,
    "longitude": 121.4737
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  ],
  "priceRange": "$$",
  "currenciesAccepted": "CNY",
  "areaServed": {
    "@type": "Country",
    "name": "中国"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "设计服务",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "零售设计" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "品牌空间设计" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "商业设计" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "设计管理" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "酒店、餐饮、会所及住宅设计" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "建筑及空间设计" } }
    ]
  }
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "YEAHSTUDIO 隐也设计工作室提供哪些设计服务？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "YEAHSTUDIO 提供零售设计、品牌空间设计、商业设计、设计管理、酒店设计、餐饮设计、会所设计、住宅设计及建筑空间设计等全方位设计服务，致力于通过细节、光影与材质为每个项目注入独特的叙事性与美学氛围。"
      }
    },
    {
      "@type": "Question",
      "name": "YEAHSTUDIO 工作室位于哪里？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "YEAHSTUDIO 隐也设计工作室位于上海市嘉定区栖林路425弄7号，服务范围覆盖全国各地。"
      }
    },
    {
      "@type": "Question",
      "name": "如何联系 YEAHSTUDIO 隐也设计工作室？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "您可以通过电话 +86-189-1826-8808 或邮件 wextin@foxmail.com 联系我们，工作时间为周一至周五 09:00–18:00。"
      }
    }
  ]
};

/** Defers rendering until the placeholder enters the viewport */
function LazySection({ children, minHeight = "200px" }: { children: React.ReactNode; minHeight?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={!visible ? { minHeight } : undefined}>
      {visible && (
        <Suspense fallback={<div style={{ minHeight }} />}>
          {children}
        </Suspense>
      )}
    </div>
  );
}

export default function Home() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setShowScrollTop(window.scrollY > 500);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="min-h-screen bg-white">
      <SchemaJsonLD schema={websiteSchema} id="website" />
      <SchemaJsonLD schema={organizationSchema} id="organization" />
      <SchemaJsonLD schema={webPageSchema} id="webpage" />
      <SchemaJsonLD schema={localBusinessSchema} id="local-business" />
      <SchemaJsonLD schema={faqSchema} id="faq" />
      <Navbar />
      <HeroCarousel />
      <LazySection minHeight="400px">
        <NewsSection />
      </LazySection>
      <LazySection minHeight="600px">
        <ProjectsSection />
      </LazySection>
      <LazySection minHeight="600px">
        <AboutUsSection />
      </LazySection>
      <LazySection minHeight="600px">
        <ServicesSection />
      </LazySection>
      <LazySection minHeight="400px">
        <ContactSection />
      </LazySection>
      <LazySection minHeight="200px">
        <Footer />
      </LazySection>

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-50 w-12 h-12 flex items-center justify-center bg-stone-900 text-white hover:bg-amber-700 transition-all duration-300 cursor-pointer ${
          showScrollTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        aria-label="Back to top"
      >
        <i className="ri-arrow-up-line text-base" />
      </button>
    </main>
  );
}
